import React, { useCallback, useEffect, useState } from 'react';

import { ModalProvider } from '@/context/ModalProvider';

import useApi from '@/hooks/useApi';
import { useDebounce } from '@/hooks/useDebounce';

import { Section } from '@/components/global/Section';
import {
  Header,
  Calculator,
  Slider,
  Tabs,
} from '@/components/Template/Template6';
import { ISlider } from '@/components/global/SmSlider/types';

import {
  TRecommendation,
  TTagList,
} from '@/services/requests/Recommendation/types/template6';
import { dispatchInteraction } from '@/services/requests/Interactions/helpers';
import { TInteractionClickComponent } from '@/services/requests/Interactions/types';

import { findLastIndex } from '@/helpers/ArrayHelper';
import { isLogged } from '@/helpers/AuthenticationHelper/SessionTokenHelper';

import useTabs from './useTabs';

type TProps = {
  data: TRecommendation[];
};

export const getTagsBycardList = (cards: TRecommendation['cards']) => {
  const result: TTagList = [];

  cards.forEach(({ id, tag: { values, description } }) => {
    const miles = values
      .find(({ type }) => type === '["miles"]')
      ?.value?.replace(/\./g, '');
    const segments = values
      .find(({ type }) => type === '["segments"]')
      ?.value?.replace(/\./g, '');

    if (miles && segments && description) {
      const existIndex = result.findIndex(
        item => item.id === `${miles}-${segments}`,
      );

      if (Object.prototype.hasOwnProperty.call(result, existIndex)) {
        result[existIndex].ids.push(id);
      } else {
        result.push({
          id: `${miles}-${segments}`,
          miles,
          segments,
          description: description
            .replace(/(\.|qualificáveis )/g, '')
            .replace(/([\d]+)([\d]{3})/g, '$1.$2'),
          ids: [id],
        });
      }
    }
  });

  return result.sort((a, b) => +a.miles - +b.miles);
};

const TemplateVitrine6 = ({ data }: TProps) => {
  const { Api } = useApi();

  const { tabItems, onTabClick, getTabActive } = useTabs(data);

  const currentData = data.find(item => item.idLiferay === getTabActive());

  const [tagsList, setTagList] = useState<TTagList>();
  const [filteredCards, setFilteredCards] = useState<TRecommendation['cards']>(
    [],
  );
  const [sliderPosition, setSliderPosition] = useState<number>();
  const debouncedValue = useDebounce<number | undefined>(sliderPosition, 1000);
  const [userInteraction, setUserInteraction] = useState<boolean>(false);

  if (!currentData) return null;

  const [cards, setCards] = useState<TRecommendation['cards']>();

  const { title, description, header, config, id } = currentData;

  const handleSliderChange: ISlider['onChange'] = async newValue => {
    if (newValue.curMax) {
      setSliderPosition(newValue.curMax);
      setUserInteraction(true);
    }
  };

  const handleResetSliderPosition = useCallback(() => {
    if (config.params.miles && tagsList && isLogged()) {
      const current = findLastIndex(tagsList, item => {
        return +item.miles < +config.params.miles;
      });

      setSliderPosition(Math.max(1, current + 1));
    }
  }, [config.params.miles, tagsList]);

  const handleTabClick = (tabId: string) => {
    setSliderPosition(undefined);
    onTabClick(tabId);
  };

  useEffect(() => {
    setCards(() => {
      return currentData.cards.map((card, i) => {
        return {
          ...card,
          id: `${card.id}_${i}`,
        };
      });
    });
  }, [currentData.cards]);

  useEffect(() => {
    if (cards) {
      const tags = getTagsBycardList(cards);
      setTagList(tags);
    }
  }, [cards]);

  useEffect(() => {
    if (cards) {
      setFilteredCards(
        tagsList?.length
          ? cards.filter(card => {
              return (
                tagsList[sliderPosition ? sliderPosition - 1 : 0]?.ids.indexOf(
                  card.id,
                ) >= 0
              );
            })
          : cards,
      );
    }
  }, [cards, sliderPosition, tagsList]);

  useEffect(() => {
    handleResetSliderPosition();
  }, [handleResetSliderPosition]);

  useEffect(() => {
    if (debouncedValue && userInteraction) {
      const miles = tagsList?.[debouncedValue - 1]?.miles;
      if (miles) {
        const interactionData = {
          recommendationId: id,
          details: {
            miles_value: miles,
          },
        };
        dispatchInteraction<TInteractionClickComponent>(
          Api,
          'click-component',
          interactionData,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Api, debouncedValue]);

  return (
    <ModalProvider theme="smiles">
      <Section
        className={`vitrine-template6 ${currentData.theme.replace(
          /"|\[|\]/g,
          '',
        )}`}
        data={data}
      >
        <div className="vitrine-template6__container">
          <Header title={title} description={description} header={header} />
          <Tabs tabItems={tabItems} handleTabClick={handleTabClick} />
          {!!tagsList?.length && tagsList.length > 1 && (
            <Calculator
              inputValue={sliderPosition ?? 1}
              handleSliderChange={handleSliderChange}
              tags={tagsList}
            />
          )}
          <Slider data={{ ...currentData, cards: filteredCards }} />
          <div
            className="vitrine-template6__more-info"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: currentData.rules.text }}
          />
        </div>
      </Section>
    </ModalProvider>
  );
};

export default TemplateVitrine6;
